/**
 * "Remember me" for the lookup screen — the two credentials the shopper just typed, so a
 * return interrupted by a phone call resumes in one tap instead of a re-type.
 *
 * Scope is deliberately the narrowest thing that works:
 *   - `sessionStorage`, never `localStorage`. It dies with the tab, so a shared phone in a
 *     shop or a family living room does not hand the next person a way back in
 *     (apps/portal/AGENTS.md: no browser storage of order data beyond the active session).
 *   - The order REFERENCE and the phone only. Never the order contents, never a line,
 *     never a price — the lookup response stays in memory in `FlowProvider` and is not
 *     written anywhere.
 *   - Keyed by merchant slug, so a shopper with two stores open does not get one store's
 *     reference offered on the other's portal.
 *
 * Shaped after `readSessionLocale`/`writeSessionLocale` in `../i18n/locale.ts`: same key
 * namespace, same "blocked storage is not an error worth surfacing" swallow.
 */

export interface RememberedShopper {
  merchantSlug: string;
  orderRef: string;
  phone: string;
}

const SESSION_SHOPPER_KEY = "sirdab.returns.shopper";

function storageOf(storage?: Storage | undefined): Storage | undefined {
  return storage ?? globalThis.sessionStorage;
}

/** Narrow the stored JSON. Anything that is not the exact shape is treated as absent. */
function parseShopper(raw: unknown): RememberedShopper | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const { merchantSlug, orderRef, phone } = record;
  if (
    typeof merchantSlug !== "string" ||
    typeof orderRef !== "string" ||
    typeof phone !== "string"
  ) {
    return null;
  }
  if (merchantSlug === "" || orderRef === "" || phone === "") return null;
  return { merchantSlug, orderRef, phone };
}

/** What this tab last looked up FOR THIS MERCHANT. Another merchant's entry reads as absent. */
export function readRememberedShopper(
  merchantSlug: string,
  storage?: Storage | undefined,
): RememberedShopper | null {
  try {
    const stored = parseShopper(
      storageOf(storage)?.getItem(SESSION_SHOPPER_KEY),
    );
    return stored !== null && stored.merchantSlug === merchantSlug
      ? stored
      : null;
  } catch {
    return null;
  }
}

export function writeRememberedShopper(
  shopper: RememberedShopper,
  storage?: Storage | undefined,
): void {
  try {
    storageOf(storage)?.setItem(SESSION_SHOPPER_KEY, JSON.stringify(shopper));
  } catch {
    /* Private mode / blocked storage costs the shopper one re-type, not an error screen. */
  }
}

/** Called when the shopper says "a different order" — and on nothing else implicitly. */
export function clearRememberedShopper(storage?: Storage | undefined): void {
  try {
    storageOf(storage)?.removeItem(SESSION_SHOPPER_KEY);
  } catch {
    /* See above. */
  }
}
